import useCreateOrder from '@/hooks/order/useCreateOrder';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Button,
  ButtonBase,
  Dialog,
  Drawer,
  IconButton,
  Radio,
  styled,
  useMediaQuery,
} from '@mui/material';
import { useContext, useState } from 'react';
import { EPurchaseSectionStep, PurchaseSectionContext } from '.';
import { ColorBankIcon, ColorVNPAYIcon } from '../svgIcon/color';
import PurchaseContactSection from './component';

enum EPaymentMethod {
  Banking = 'Banking',
  VNPAY = 'VNPAY',
}

const PaymentMethod = () => {
  const { setOpenStep, openStep } = useContext(PurchaseSectionContext);
  const toggle = () => setOpenStep(undefined);

  const matches = useMediaQuery('(min-width:768px)');
  return matches ? (
    <Dialog
      open={openStep === EPurchaseSectionStep.PaymentMethod}
      onClose={toggle}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 120,
      }}
    >
      <PaymentMethod.Header toggle={toggle} />
      <PaymentMethod.MethodSelection />
    </Dialog>
  ) : (
    <Drawer
      anchor={'bottom'}
      open={openStep === EPurchaseSectionStep.PaymentMethod}
      onClose={toggle}
      PaperProps={{
        className: '!bg-[#FCFCFC] !rounded-[10px]',
      }}
      sx={{
        zIndex: 120,
      }}
    >
      <PaymentMethod.Header toggle={toggle} />
      <PaymentMethod.MethodSelection />
    </Drawer>
  );
};

PaymentMethod.Header = function Header({ toggle }: { toggle: () => void }) {
  return (
    <div className="flex items-center justify-between p-4 text-[#000000DE] md:p-6">
      <h1 className="text-2xl font-bold leading-[140%]">
        Phương thức thanh toán
      </h1>
      <IconButton onClick={toggle}>
        <CloseRoundedIcon className="text-[#000000DE]" />
      </IconButton>
    </div>
  );
};

const ButtonBaseCustom = styled(ButtonBase)(
  () => `
            width: 366px;
            margin: 0px;
            padding: 16px 12px;
            position: relative;
            border-radius: 8px;
            box-shadow: 0px 6px 20px 0px rgba(0, 0, 0, 0.10);
            justify-content: start;
            gap: 16px;

            &:active {
              background-color: #ECF5FA;
              border: 2px solid #009FE4;
              padding: 14px 10px;
            }

            .MuiTouchRipple-root {
              color: #009CE0;
            }

            &.Mui-disabled {
              box-shadow: none;
              background-color: #EBEBEB;
            }
            
            .MuiRadio-root {
              padding: 0;
            }
`
);

PaymentMethod.MethodSelection = function MethodSelection() {
  const [value, setValue] = useState<EPaymentMethod>(EPaymentMethod.VNPAY);

  const labelMapping: { [key: string]: React.ReactNode } = {
    [EPaymentMethod.Banking]: (
      <div className="flex w-full items-center justify-between">
        <p className="text-base font-medium">Thanh toán ngân hàng</p>
        <ColorBankIcon />
      </div>
    ),
    [EPaymentMethod.VNPAY]: (
      <div className="flex w-full items-center justify-between">
        <p className="text-base font-medium">Thanh toán nhanh</p>
        <ColorVNPAYIcon />
      </div>
    ),
  };

  return (
    <>
      <div className="flex w-full flex-col items-center gap-4 px-6 py-6 md:py-10">
        <ButtonBaseCustom
          onClick={() => setValue(EPaymentMethod.VNPAY)}
          disabled
        >
          <Radio
            checked={value === EPaymentMethod.Banking}
            value={EPaymentMethod.Banking}
            disabled
          />
          {labelMapping[EPaymentMethod.Banking]}
        </ButtonBaseCustom>
        <ButtonBaseCustom onClick={() => setValue(EPaymentMethod.VNPAY)}>
          <Radio
            checked={value === EPaymentMethod.VNPAY}
            value={EPaymentMethod.VNPAY}
          />
          {labelMapping[EPaymentMethod.VNPAY]}
        </ButtonBaseCustom>
      </div>
      <div className="mt-6 flex flex-col-reverse gap-4 px-4 py-4 md:mt-0 md:flex-col md:gap-2">
        <PurchaseContactSection />
        <hr className="h-[1px] bg-[#E0E0E0] md:hidden" />
        <PaymentMethod.ConfirmButton disabled={!value} value={value} />
      </div>
    </>
  );
};

PaymentMethod.ConfirmButton = function ConfirmButton({
  disabled,
  value,
}: {
  disabled: boolean;
  value: EPaymentMethod;
}) {
  const { setOpenStep } = useContext(PurchaseSectionContext);
  const matches = useMediaQuery('(min-width:768px)');
  const { order } = useContext(PurchaseSectionContext);
  const { redirectToPaymentPage } = useCreateOrder({});
  const handleClickConfirm = () => {
    if (value === EPaymentMethod.Banking) {
      setOpenStep(EPurchaseSectionStep.TransactionInfo);
      return;
    }
    redirectToPaymentPage(order.orderId);
  };

  return (
    <div className="md:ml-auto md:w-fit">
      {matches && (
        <Button
          onClick={() => setOpenStep(undefined)}
          variant="low_emphasis_v1"
          className="!mr-2 !py-2"
        >
          Huỷ
        </Button>
      )}
      <Button
        onClick={handleClickConfirm}
        fullWidth={!matches}
        disabled={disabled}
        variant="high_emphasis_v1"
        className="!py-2"
      >
        Tiếp tục
      </Button>
    </div>
  );
};

export default PaymentMethod;
